import { Button } from '@heidi/design-system/button';
import { RotateCw } from 'lucide-react';
import { useIntl, type MessageDescriptor } from 'react-intl';

import type { RefKind } from '../../contracts';
import { ChatTokenChip, OBJECT_KIND_ICON, TokenMark } from './ChatTokenChip';
import {
  ObjectReferenceHoverCard,
  type ObjectReferenceSummary,
} from './ObjectReferenceHoverCard';
import { referenceChipMessages as messages } from './reference-chip.messages';

export type ReferenceState =
  | { status: 'loading' }
  | {
      status: 'resolved';
      label: string;
      onOpen?: () => void;
      summary?: ObjectReferenceSummary;
      patientName?: string;
      avatarSeed?: string;
    }
  | { status: 'error'; retry: () => void }
  | { status: 'unavailable' };

const KIND_UNAVAILABLE: Record<RefKind, MessageDescriptor> = {
  patient: messages.patientUnavailable,
  session: messages.sessionUnavailable,
  document: messages.documentUnavailable,
  template: messages.templateUnavailable,
  routine: messages.routineUnavailable,
  project: messages.projectUnavailable,
  task: messages.taskUnavailable,
  letter: messages.letterUnavailable,
  file: messages.fileUnavailable,
  collection: messages.collectionUnavailable,
  source: messages.sourceUnavailable,
};

function KindGlyph({ kind }: { readonly kind: RefKind }): JSX.Element {
  const Icon = OBJECT_KIND_ICON[kind];
  return <Icon className="size-3 shrink-0" aria-hidden />;
}

function ResolvedReference({
  kind,
  label,
  patientName,
  avatarSeed,
  summary,
  onOpen,
}: {
  readonly kind: RefKind;
  readonly label: string;
  readonly patientName?: string;
  readonly avatarSeed?: string;
  readonly summary?: ObjectReferenceSummary;
  readonly onOpen?: () => void;
}): JSX.Element {
  const intl = useIntl();
  const token = (
    <ChatTokenChip
      label={label}
      kindNoun={intl.formatMessage(messages[kind])}
      mark={
        <TokenMark
          kind={kind}
          label={label}
          patientName={patientName}
          avatarSeed={avatarSeed ?? summary?.avatarSeed}
        />
      }
      dataKind={kind}
      onClick={onOpen}
      className="max-w-full"
    />
  );
  return summary ? (
    <ObjectReferenceHoverCard object={summary} clickable={onOpen !== undefined}>
      {token}
    </ObjectReferenceHoverCard>
  ) : (
    token
  );
}

export function ReferenceChip({
  kind,
  state,
}: {
  readonly kind: RefKind;
  readonly state: ReferenceState;
}): JSX.Element {
  const intl = useIntl();

  switch (state.status) {
    case 'loading':
      return (
        <span
          data-testid="chat-object-reference-loading"
          data-object-kind={kind}
          aria-busy="true"
          className="mx-0.5 inline-flex items-center gap-1 align-baseline text-text-secondary"
        >
          <KindGlyph kind={kind} />
          {/* Not the DS Skeleton: a <div> is invalid inside the chip's <p>. */}
          <span
            aria-hidden
            className="inline-block h-3 w-16 animate-pulse rounded-md bg-background-secondary [[data-user-message]_&]:bg-background-secondary-hover"
          />
          <span className="sr-only">{intl.formatMessage(messages[kind])}</span>
        </span>
      );
    case 'resolved':
      return (
        <ResolvedReference
          kind={kind}
          label={state.label}
          patientName={state.patientName}
          avatarSeed={state.avatarSeed}
          summary={state.summary}
          onOpen={state.onOpen}
        />
      );
    case 'error':
      return (
        <span className="inline-flex max-w-full items-center gap-1 align-baseline">
          <ChatTokenChip
            label={intl.formatMessage(messages[kind])}
            mark={<KindGlyph kind={kind} />}
            dataKind={kind}
          />
          <Button
            type="button"
            variant="ghost"
            size="xs"
            iconOnly
            flush="all"
            aria-label={intl.formatMessage(messages.retry)}
            onClick={state.retry}
            className="shrink-0 text-text-secondary"
          >
            <RotateCw aria-hidden />
          </Button>
        </span>
      );
    case 'unavailable':
      return (
        <ChatTokenChip
          label={intl.formatMessage(KIND_UNAVAILABLE[kind])}
          mark={<KindGlyph kind={kind} />}
          dataKind={kind}
          disabled
        />
      );
  }
}
